import React from 'react';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Send, Loader2,
  CheckCircle, Clock, AlertCircle, ReceiptText, Lock, ShieldCheck
} from 'lucide-react';
import { Card, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { formatMontant } from './etatSalairesUtils';

const formatMonthInputValue = (date) => {
  const safeDate = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  return `${safeDate.getFullYear()}-${String(safeDate.getMonth() + 1).padStart(2, '0')}`;
};

const EtatSalairesHeader = ({
  monthLabel,
  enLecture = false,
  selectedDate,
  setSelectedDate,
  formatDateForDisplay,
  goToPreviousMonth,
  goToNextMonth,
  onBackToDashboard,
  montantDotation,
  effectif,
  dejaPayes,
  sendingDotation,
  dotationSent,
  canRequestSalaryDotation,
  canValidateEtat,
  etatValidated,
  validatingEtat,
  onDemanderDotation,
  onValiderEtat
}) => {
  const dotationDisabled = sendingDotation || dotationSent || montantDotation <= 0 || !canRequestSalaryDotation;
  const validationDisabled = validatingEtat || etatValidated || !canValidateEtat;

  const handleMonthChange = (event) => {
    const [year, month] = String(event.target.value || '').split('-').map(Number);
    if (!year || !month) return;
    setSelectedDate(new Date(year, month - 1, 1, 12, 0, 0));
  };

  return (
    <>
      <Card className="border-0 shadow-sm overflow-visible relative z-10">
        <CardHeader className="bg-[#246990] text-white rounded-t-lg overflow-visible">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-start gap-3">
              <button type="button" onClick={onBackToDashboard} className="mt-1 p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors" title="Retour">
                <ArrowLeft size={18} />
              </button>
              <div>
                <CardTitle className="text-2xl">État des salaires</CardTitle>
                <p className="text-white text-sm mt-1 font-semibold">Lecture du net à doter avant demande à la caisse centrale</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {/* Pendant le chargement, pas de mois : « Mois non défini » faisait croire
                      à un défaut de réglage. Même message que le Portefeuille. */}
                  <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-white/15 text-white border border-white/30">
                    {enLecture ? "Lecture de l'état en cours…" : `Mois lu : ${monthLabel}`}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-8 items-center gap-1 rounded-md bg-white/10 px-1 backdrop-blur-sm">
                <button type="button" onClick={goToPreviousMonth} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-white transition-colors hover:bg-white/20" title="Mois précédent">
                  <ChevronLeft size={18} />
                </button>
                <label className="relative block">
                  <span className="sr-only">Choisir le mois de paie</span>
                  <input type="month" value={formatMonthInputValue(selectedDate)} onChange={handleMonthChange} className="etat-salaires-month-input h-8 w-[132px] rounded-md border border-white/25 bg-transparent px-3 text-xs font-semibold text-white outline-none transition hover:bg-white/10 focus:ring-2 focus:ring-white/70" title={formatDateForDisplay(selectedDate)} />
                </label>
                <button type="button" onClick={goToNextMonth} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-white transition-colors hover:bg-white/20" title="Mois suivant">
                  <ChevronRight size={18} />
                </button>
              </div>

              <Button size="sm" type="button" className="bg-white text-[#246990] hover:bg-white/90 transition-colors disabled:bg-white/40 disabled:text-white/70 disabled:cursor-not-allowed" onClick={onValiderEtat} disabled={validationDisabled}>
                {validatingEtat ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ShieldCheck size={16} className="mr-2" />}
                {etatValidated ? 'État validé' : "Valider l'état"}
              </Button>
              <Button size="sm" className="bg-white text-[#246990] hover:bg-white/90 transition-colors disabled:bg-white/40 disabled:text-white/70 disabled:cursor-not-allowed" onClick={onDemanderDotation} disabled={dotationDisabled}>
                {sendingDotation ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
                {dotationSent ? 'Demande envoyée' : 'Demander la dotation'}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="mt-6 p-4 bg-[#f9f0ee] rounded-lg border border-[#ae7467]/20 flex items-center">
        <div className="p-2 bg-[#ae7467] rounded-full mr-4"><ReceiptText className="text-white" size={20} /></div>
        <div className="flex-1">
          <h5 className="font-medium text-[#ae7467]">Information importante</h5>
          <p className="text-sm text-gray-700 leading-relaxed">Cet écran <strong>prépare la demande de dotation</strong> : il calcule le net à payer à partir du brut, des retenues et des avances du mois. <strong>Aucun paiement n'est créé ici.</strong><br /><br />La demande envoyée à la caisse centrale joint automatiquement le détail des salaires du mois en justificatif.<span className="font-semibold text-[#ae7467]"> Le paiement effectif n'est possible qu'une fois la dotation validée et l'état validé.</span></p>
        </div>
      </div>
    </>
  );
};

export default EtatSalairesHeader;
